import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Bot, Sparkles, Plus, Send, Trash2, Check, X, CheckCircle2,
  AlertTriangle, Loader2, MessageSquare, ClipboardList, ShieldAlert,
  Pencil,
} from "lucide-react";
import { Navigate } from "react-router-dom";
import api from "../lib/api";
import { Card, Badge, Button, PageHeader, PageLoader } from "../lib/ui";
import { useToast } from "../lib/toast";
import { useAuth } from "../context/AuthContext";

/* Central Flow AI Operations Assistant — Owner-only.
   Architecture: Chat → AI Parse → Preview → Confirm → Save.
   The AI NEVER writes to the DB directly — every operation is staged, previewed,
   and executed only after the Owner clicks Approve.
   Sidebar-left: sessions list · Center: chat · Right: preview / staged ops. */

const OP_STATUS_TONE = {
  pending: "warn", invalid: "danger", executed: "success", failed: "danger", rejected: "mute",
};
const OP_STATUS_LABEL = {
  pending: "Menunggu Persetujuan", invalid: "Preview Gagal",
  executed: "Sudah Dijalankan", failed: "Gagal", rejected: "Ditolak",
};
const OP_STATUS_ICON = {
  pending: AlertTriangle, invalid: ShieldAlert, executed: CheckCircle2,
  failed: X, rejected: X,
};


function SessionList({ sessions, activeId, onSelect, onNew, onDelete }) {
  return (
    <Card className="flex h-full flex-col p-3" data-testid="ai-ops-sessions">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-ink-mute">
          <MessageSquare size={14} /> Chat History
        </div>
        <Button size="sm" onClick={onNew} data-testid="ai-ops-new-chat">
          <Plus size={14} /> Chat Baru
        </Button>
      </div>
      <ul className="flex-1 space-y-1 overflow-y-auto">
        {(sessions || []).map((s) => (
          <li key={s.id}>
            <div
              className={`group flex items-center gap-2 rounded-lg p-2 text-sm transition ${
                s.id === activeId ? "bg-orange-50 text-orange-900" : "hover:bg-slate-50"
              }`}
            >
              <button
                onClick={() => onSelect(s.id)}
                className="min-w-0 flex-1 truncate text-left"
                data-testid={`ai-ops-session-${s.id}`}
              >
                {s.title || "Chat baru"}
              </button>
              <button
                onClick={() => onDelete(s.id)}
                className="opacity-0 group-hover:opacity-100"
                data-testid={`ai-ops-session-delete-${s.id}`}
                title="Hapus"
              >
                <Trash2 size={14} className="text-ink-mute hover:text-red-500" />
              </button>
            </div>
          </li>
        ))}
        {!sessions?.length && (
          <li className="p-3 text-center text-xs text-ink-mute">Belum ada chat. Klik &quot;Chat Baru&quot; untuk mulai.</li>
        )}
      </ul>
    </Card>
  );
}

function ChatBubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm ${
          isUser ? "bg-orange-500 text-white" : "bg-slate-100 text-ink"
        }`}
        data-testid={`ai-ops-msg-${msg.id}`}
      >
        {!isUser && <div className="mb-0.5 flex items-center gap-1.5 text-xs font-bold text-orange-600"><Bot size={12} /> Central Flow AI</div>}
        <p className="whitespace-pre-wrap">{msg.content}</p>
        {(msg.operation_ids || []).length > 0 && (
          <p className="mt-1 text-[11px] italic opacity-75">
            {msg.operation_ids.length} operasi ter-stage (lihat panel kanan)
          </p>
        )}
      </div>
    </div>
  );
}

function ChatPanel({ session, onSend, sending }) {
  const [text, setText] = useState("");
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [session?.messages?.length, sending]);

  const submit = () => {
    const t = text.trim();
    if (!t || sending) return;
    onSend(t);
    setText("");
  };

  return (
    <Card className="flex h-full flex-col p-0" data-testid="ai-ops-chat">
      <div className="border-b border-line p-3">
        <p className="text-sm font-bold text-ink">{session?.title || "Chat baru"}</p>
        <p className="text-xs text-ink-mute">Percakapan Anda dengan AI Operations Assistant</p>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="ai-ops-messages">
        {!session?.messages?.length && (
          <div className="rounded-xl bg-slate-50 p-4 text-center text-sm text-ink-mute" data-testid="ai-ops-empty">
            <Sparkles size={18} className="mx-auto mb-2 text-orange-500" />
            <p className="font-semibold text-ink">Mulai percakapan</p>
            <p className="mt-1 text-xs">Contoh: <em>&quot;Buat 5 produk baru: Tahu Cabe, Tempe Mendoan, Tahu Isi, Bakwan, Cireng — semua kategori Snack, satuan pcs, harga 5000, 6000, 4000, 3500, 4500.&quot;</em></p>
          </div>
        )}
        {(session?.messages || []).map((m) => <ChatBubble key={m.id} msg={m} />)}
        {sending && (
          <div className="flex justify-start">
            <div className="rounded-2xl bg-slate-100 px-3.5 py-2 text-sm text-ink">
              <div className="flex items-center gap-1.5 text-xs font-bold text-orange-600"><Bot size={12} /> Central Flow AI</div>
              <div className="mt-1 flex items-center gap-1.5 text-ink-mute">
                <Loader2 size={14} className="animate-spin" /> Sedang menganalisis…
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <div className="border-t border-line p-3">
        <div className="flex items-end gap-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit();
            }}
            placeholder="Tulis instruksi… (Ctrl+Enter untuk kirim)"
            rows={2}
            className="flex-1 resize-none rounded-lg border border-line bg-white px-3 py-2 text-sm focus:border-orange-400 focus:outline-none"
            disabled={sending}
            data-testid="ai-ops-input"
          />
          <Button onClick={submit} disabled={!text.trim() || sending} data-testid="ai-ops-send">
            <Send size={14} /> Kirim
          </Button>
        </div>
      </div>
    </Card>
  );
}

function PreviewField({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-line/50 py-1.5 last:border-0 text-xs">
      <span className="text-ink-mute">{label}</span>
      <span className="text-right font-semibold text-ink">{String(value ?? "-")}</span>
    </div>
  );
}

/* Structured Edit modal — renders a form generated from the plugin's input_schema
   (never expose raw JSON per Owner directive). Each schema property becomes a
   typed input; array fields render as a textarea with JSON hint (used only for
   nested list payloads like recipe ingredients). */
function EditOperationModal({ op, onClose, onSave }) {
  const schema = op?.input_schema || { properties: {}, required: [] };
  const [values, setValues] = useState(() => ({ ...(op?.payload || {}) }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const setField = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

  const submit = async () => {
    setSaving(true);
    setErr(null);
    try {
      // Strip empty strings so backend preview treats them as absent (matches AI behavior)
      const clean = {};
      Object.entries(values).forEach(([k, v]) => {
        if (v === "" || v === null || v === undefined) return;
        clean[k] = v;
      });
      await onSave(clean);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const props = schema.properties || {};
  const required = new Set(schema.required || []);
  const keys = Object.keys(props);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="ai-ops-edit-modal"
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <div>
            <p className="text-sm font-bold text-ink">Edit Operasi</p>
            <p className="text-xs text-ink-mute">{op.tool} · sesuaikan payload sebelum menyetujui</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 hover:bg-slate-100" data-testid="ai-ops-edit-close">
            <X size={16} />
          </button>
        </div>
        {err && (
          <div className="mb-3 rounded-lg bg-red-50 px-2.5 py-2 text-xs text-red-700">{err}</div>
        )}
        <div className="space-y-2">
          {keys.length === 0 && (
            <p className="text-xs text-ink-mute">Skema untuk plugin ini tidak tersedia.</p>
          )}
          {keys.map((k) => {
            const spec = props[k] || {};
            const v = values[k];
            const label = (
              <label className="mb-1 flex items-center gap-1 text-xs font-semibold text-ink">
                {k}{required.has(k) && <span className="text-red-500">*</span>}
                {spec.description && (
                  <span className="text-[10px] font-normal text-ink-mute">— {spec.description}</span>
                )}
              </label>
            );
            const testId = `ai-ops-edit-field-${k}`;
            if (spec.type === "boolean") {
              return (
                <div key={k}>
                  {label}
                  <select
                    value={v === true ? "true" : v === false ? "false" : ""}
                    onChange={(e) => setField(k, e.target.value === "" ? null : e.target.value === "true")}
                    className="w-full rounded-lg border border-line bg-white px-2 py-1.5 text-sm"
                    data-testid={testId}
                  >
                    <option value="">—</option>
                    <option value="true">true</option>
                    <option value="false">false</option>
                  </select>
                </div>
              );
            }
            if (Array.isArray(spec.enum) && spec.enum.length) {
              return (
                <div key={k}>
                  {label}
                  <select
                    value={v ?? ""}
                    onChange={(e) => setField(k, e.target.value || null)}
                    className="w-full rounded-lg border border-line bg-white px-2 py-1.5 text-sm"
                    data-testid={testId}
                  >
                    <option value="">—</option>
                    {spec.enum.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
              );
            }
            if (spec.type === "array" || spec.type === "object") {
              return (
                <div key={k}>
                  {label}
                  <textarea
                    value={v === undefined || v === null ? "" : JSON.stringify(v, null, 2)}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw.trim() === "") { setField(k, null); return; }
                      try { setField(k, JSON.parse(raw)); }
                      catch { setField(k, raw); }  // keep as string; server will 400 preview if malformed
                    }}
                    rows={4}
                    className="w-full rounded-lg border border-line bg-white px-2 py-1.5 font-mono text-xs"
                    placeholder="Isi JSON array/object"
                    data-testid={testId}
                  />
                </div>
              );
            }
            const inputType = spec.type === "number" || spec.type === "integer" ? "number"
              : (k.toLowerCase().includes("password") ? "password" : "text");
            return (
              <div key={k}>
                {label}
                <input
                  type={inputType}
                  value={v ?? ""}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (inputType === "number" && val !== "") val = Number(val);
                    setField(k, val === "" ? null : val);
                  }}
                  className="w-full rounded-lg border border-line bg-white px-2 py-1.5 text-sm"
                  data-testid={testId}
                />
              </div>
            );
          })}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving} data-testid="ai-ops-edit-cancel">
            Batal
          </Button>
          <Button onClick={submit} disabled={saving} data-testid="ai-ops-edit-save">
            {saving ? <><Loader2 size={13} className="animate-spin" /> Menyimpan…</>
              : <><Check size={13} /> Simpan & Preview Ulang</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function OperationCard({ op, onApprove, onReject, onEdit, working }) {
  const StatusIcon = OP_STATUS_ICON[op.status] || AlertTriangle;
  const preview = op.preview || {};
  const canDecide = op.status === "pending";
  const canEdit = op.status === "pending" || op.status === "invalid";
  return (
    <div className="rounded-xl border border-line bg-white p-3" data-testid={`ai-ops-op-${op.id}`}>
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-ink">{preview.title || op.tool}</p>
          <p className="text-xs text-ink-mute">{preview.summary || ""}</p>
        </div>
        <Badge tone={OP_STATUS_TONE[op.status] || "mute"} className="shrink-0">
          <StatusIcon size={10} className="mr-1 inline" />
          {OP_STATUS_LABEL[op.status] || op.status}
        </Badge>
      </div>
      {preview.error && (
        <div className="mb-2 rounded-lg bg-red-50 px-2.5 py-2 text-xs text-red-700">
          {preview.error}
        </div>
      )}
      {(preview.fields || []).length > 0 && (
        <div className="mb-2 rounded-lg bg-slate-50 px-2.5 py-1.5">
          {preview.fields.map((f, i) => (
            <PreviewField key={i} label={f.label} value={f.value} />
          ))}
        </div>
      )}
      {(preview.warnings || []).length > 0 && (
        <ul className="mb-2 space-y-0.5 text-xs text-amber-700">
          {preview.warnings.map((w, i) => (
            <li key={i} className="flex items-start gap-1">
              <AlertTriangle size={11} className="mt-0.5 shrink-0" />
              <span>{w}</span>
            </li>
          ))}
        </ul>
      )}
      {op.error && (
        <div className="mb-2 rounded-lg bg-red-50 px-2.5 py-2 text-xs text-red-700">
          <b>Error:</b> {op.error}
        </div>
      )}
      {op.status === "executed" && op.result && (
        <div className="mb-2 rounded-lg bg-emerald-50 px-2.5 py-2 text-xs text-emerald-800">
          <b>Dieksekusi.</b> {op.result?.name || op.result?.id || JSON.stringify(op.result)}
        </div>
      )}
      {canDecide && (
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => onApprove(op.id)}
            disabled={working}
            data-testid={`ai-ops-approve-${op.id}`}
          >
            <Check size={13} /> Setujui
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onEdit(op)}
            disabled={working}
            data-testid={`ai-ops-edit-${op.id}`}
          >
            <Pencil size={13} /> Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onReject(op.id)}
            disabled={working}
            data-testid={`ai-ops-reject-${op.id}`}
          >
            <X size={13} /> Tolak
          </Button>
        </div>
      )}
      {!canDecide && canEdit && (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onEdit(op)}
            disabled={working}
            data-testid={`ai-ops-edit-${op.id}`}
          >
            <Pencil size={13} /> Edit & Perbaiki
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onReject(op.id)}
            disabled={working}
            data-testid={`ai-ops-reject-${op.id}`}
          >
            <X size={13} /> Tolak
          </Button>
        </div>
      )}
      {(op.edited_by_name || op.decided_by_name) && (
        <p className="mt-2 text-[10px] text-ink-mute">
          {op.edited_by_name && <span>Diedit oleh {op.edited_by_name} · </span>}
          {op.decided_by_name && <span>Diputuskan oleh {op.decided_by_name}</span>}
        </p>
      )}
    </div>
  );
}

function PreviewPanel({ session, onApprove, onReject, onEdit, onApproveAll, working }) {
  const ops = session?.operations || [];
  const pending = ops.filter((o) => o.status === "pending");
  const invalid = ops.filter((o) => o.status === "invalid");
  const done = ops.filter((o) => ["executed", "failed", "rejected"].includes(o.status));

  return (
    <Card className="flex h-full flex-col p-0" data-testid="ai-ops-preview">
      <div className="flex items-center justify-between border-b border-line p-3">
        <div className="flex items-center gap-2">
          <ClipboardList size={16} className="text-orange-500" />
          <div>
            <p className="text-sm font-bold text-ink">Preview Operasi</p>
            <p className="text-xs text-ink-mute">
              {pending.length} pending · {invalid.length} preview error · {done.length} selesai
            </p>
          </div>
        </div>
        {pending.length > 1 && (
          <Button size="sm" onClick={onApproveAll} disabled={working} data-testid="ai-ops-approve-all">
            <Check size={13} /> Setujui Semua ({pending.length})
          </Button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {!ops.length && (
          <div className="rounded-xl bg-slate-50 p-4 text-center text-sm text-ink-mute">
            Belum ada operasi. Kirim pesan untuk membuat rencana operasi.
          </div>
        )}
        {ops.map((op) => (
          <OperationCard
            key={op.id}
            op={op}
            onApprove={onApprove}
            onReject={onReject}
            onEdit={onEdit}
            working={working}
          />
        ))}
      </div>
    </Card>
  );
}


export default function AIOpsAssistant() {
  const { user } = useAuth();
  const toast = useToast();
  const [sessions, setSessions] = useState([]);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [working, setWorking] = useState(false);
  const [editingOp, setEditingOp] = useState(null);

  const isAllowed = !user || ["OWNER", "SUPER_ADMIN"].includes(user.role);

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await api.get("/ai-ops/sessions");
      setSessions(data.sessions || []);
      return data.sessions || [];
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat sesi");
      return [];
    }
  }, [toast]);

  const openSession = useCallback(async (sid) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/ai-ops/sessions/${sid}`);
      setSession(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat chat");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const newChat = useCallback(async () => {
    try {
      const { data } = await api.post("/ai-ops/sessions", {});
      await loadSessions();
      setSession(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membuat chat");
    }
  }, [loadSessions, toast]);

  const deleteSession = useCallback(async (sid) => {
    if (!window.confirm("Hapus chat ini?")) return;
    try {
      await api.delete(`/ai-ops/sessions/${sid}`);
      const list = await loadSessions();
      if (session?.id === sid) {
        setSession(list[0] ? await api.get(`/ai-ops/sessions/${list[0].id}`).then(r => r.data) : null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus chat");
    }
  }, [loadSessions, session, toast]);

  const sendMessage = useCallback(async (text) => {
    if (!session) return;
    setSending(true);
    // Optimistic user message
    const optimistic = {
      id: `tmp-${Date.now()}`, role: "user", content: text,
      created_at: new Date().toISOString(),
    };
    setSession((s) => s ? { ...s, messages: [...(s.messages || []), optimistic] } : s);
    try {
      await api.post(`/ai-ops/sessions/${session.id}/message`, { message: text });
      await openSession(session.id);
      await loadSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim pesan");
    } finally {
      setSending(false);
    }
  }, [session, openSession, loadSessions, toast]);

  const approveOp = useCallback(async (opId) => {
    setWorking(true);
    try {
      const { data } = await api.post(`/ai-ops/operations/${opId}/approve`);
      if (data.status === "failed") toast.error(data.error || "Operasi gagal");
      else toast.success("Operasi berhasil dijalankan");
      await openSession(session.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyetujui");
    } finally {
      setWorking(false);
    }
  }, [session, openSession, toast]);

  const rejectOp = useCallback(async (opId) => {
    setWorking(true);
    try {
      await api.post(`/ai-ops/operations/${opId}/reject`);
      await openSession(session.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menolak");
    } finally {
      setWorking(false);
    }
  }, [session, openSession, toast]);

  const approveAll = useCallback(async () => {
    if (!session) return;
    setWorking(true);
    try {
      const { data } = await api.post(`/ai-ops/sessions/${session.id}/approve-all`);
      const failed = data.results.filter((r) => r.status === "failed").length;
      const ok = data.results.filter((r) => r.status === "executed").length;
      toast[failed ? "error" : "success"](`${ok} berhasil · ${failed} gagal`);
      await openSession(session.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal batch approve");
    } finally {
      setWorking(false);
    }
  }, [session, openSession, toast]);

  const saveEdit = useCallback(async (newPayload) => {
    if (!editingOp) return;
    await api.post(`/ai-ops/operations/${editingOp.id}/edit`, { payload: newPayload });
    setEditingOp(null);
    toast.success("Preview diperbarui");
    await openSession(session.id);
  }, [editingOp, session, openSession, toast]);

  useEffect(() => {
    (async () => {
      const list = await loadSessions();
      if (list.length) {
        await openSession(list[0].id);
      }
      setLoading(false);
    })();
  }, [loadSessions, openSession]);

  if (!isAllowed) {
    return <Navigate to="/" replace />;
  }

  const hasActive = !!session;

  return (
    <div className="space-y-3 pb-6" data-testid="ai-ops-assistant-page">
      <PageHeader
        title="AI Operations Assistant"
        subtitle="Kelola master data lewat percakapan. Chat → Preview → Konfirmasi → Simpan"
        icon={Sparkles}
      >
        <Badge tone="warn">Owner-Only</Badge>
      </PageHeader>
      {loading && !hasActive ? (
        <PageLoader />
      ) : (
        <div className="grid gap-3 lg:grid-cols-12" style={{ minHeight: "72vh" }}>
          <div className="lg:col-span-2 h-[72vh]">
            <SessionList
              sessions={sessions}
              activeId={session?.id}
              onSelect={openSession}
              onNew={newChat}
              onDelete={deleteSession}
            />
          </div>
          <div className="lg:col-span-6 h-[72vh]">
            {hasActive ? (
              <ChatPanel session={session} onSend={sendMessage} sending={sending} />
            ) : (
              <Card className="flex h-full items-center justify-center p-6">
                <div className="text-center">
                  <Bot size={40} className="mx-auto mb-2 text-orange-500" />
                  <p className="mb-3 text-sm text-ink-mute">Belum ada chat aktif</p>
                  <Button onClick={newChat} data-testid="ai-ops-start-first">
                    <Plus size={14} /> Mulai Chat Baru
                  </Button>
                </div>
              </Card>
            )}
          </div>
          <div className="lg:col-span-4 h-[72vh]">
            {hasActive && (
              <PreviewPanel
                session={session}
                onApprove={approveOp}
                onReject={rejectOp}
                onEdit={setEditingOp}
                onApproveAll={approveAll}
                working={working}
              />
            )}
          </div>
        </div>
      )}
      {editingOp && (
        <EditOperationModal
          op={editingOp}
          onClose={() => setEditingOp(null)}
          onSave={saveEdit}
        />
      )}
    </div>
  );
}
